import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../services/AxiosInstance';
import { sortSubscriptionPlansBySortOrder } from '../../../utils/subscriptionPlansSort';
import './ProductDocumentPricing.css';

const ProductDocumentPricing = () => {
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    const getPlans = async () => {
      try {
        const response = await axiosInstance.get('/subscription/plans');
        setSubscriptionPlans(sortSubscriptionPlansBySortOrder([...(response.data?.data || [])]));
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      }
    };
    getPlans();
  }, []);

  const getPlansByModule = (moduleType) => {
    return subscriptionPlans.filter((plan) => {
      if (plan.Module) {
        const moduleName = (plan.Module.name || plan.Module.display_name || '').toLowerCase();
        return moduleName === moduleType;
      }
      if (plan.module_id) {
        return moduleType === 'task' ? plan.module_id === 1 : plan.module_id === 2;
      }
      return false;
    });
  };

  const filteredPlans = getPlansByModule('document');

  const safeParse = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
        try {
          return JSON.parse(value);
        } catch (e) {
          return [value];
        }
      }
      return value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  return (
    <section className="product-document-pricing">
      <div className="container">
        <h2 className="product-document-pricing-title">Prices</h2>
        <p className="product-document-pricing-subtitle mb-4">
          Our plans are organized to suit your requirements. Start with our FREE plan option to test the
          platform. No credit card details or payment required to start the Free Plan.
        </p>

        <div className="billing-toggle-container mb-4">
          <div className="billing-toggle">
            <span className={`toggle-label ${billingCycle === 'monthly' ? 'active' : ''}`}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={billingCycle === 'yearly'}
                onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className={`toggle-label ${billingCycle === 'yearly' ? 'active' : ''}`}>Yearly</span>
          </div>
        </div>

        {filteredPlans.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <p className="text-muted">No plans available for Documents.</p>
          </div>
        ) : (
          <Row className="g-4 justify-content-center">
            {filteredPlans.map((plan) => {
              const isPopular = plan.PlanId === 2;
              return (
                <Col key={plan.PlanId} lg={4} md={6} sm={12} className="mb-4">
                  <Card className="product-document-pricing-card h-100">
                    {isPopular && (
                      <div className="popular-badge">
                        <Badge bg="warning" className="rounded-pill px-3 py-2">
                          <i className="fas fa-fire me-1"></i>Most Popular
                        </Badge>
                      </div>
                    )}
                    <div className="plan-header">
                      <Card.Title className="fw-bold mb-0">{plan.display_name}</Card.Title>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <div className="flex-grow-1">
                        <div className="pricing-section text-center">
                          <div className="price-display">
                            <span className="amount">
                              {`$${billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price}`}
                            </span>
                            <span className="period">
                              /{billingCycle === 'yearly' ? 'year' : 'month'}
                            </span>
                          </div>
                          {billingCycle === 'yearly' && plan.monthly_price && (
                            <small className="text-muted d-block mt-1">
                              {`$${plan.monthly_price}/month billed annually`}
                            </small>
                          )}
                          {plan.is_unlimited_users ? (
                            <p className="users-info">Unlimited users</p>
                          ) : (
                            plan.billing_type === 'tiered' && plan.max_users && (
                              <p className="users-info">{plan.max_users} users</p>
                            )
                          )}
                        </div>
                        {plan.description && (
                          <div className="plan-description-section text-center">
                            <p>{plan.description}</p>
                          </div>
                        )}
                        <div className="mb-2">
                          {plan.whats_included && plan.whats_included.length > 0 && (
                            <h6 className="text-muted mb-2 text-center">What&apos;s included:</h6>
                          )}
                          <ul className="list-unstyled">
                            {(() => {
                              const included =
                                plan.whats_included && plan.whats_included.length > 0
                                  ? safeParse(plan.whats_included)
                                  : plan.features_list && plan.features_list.length > 0
                                    ? safeParse(plan.features_list)
                                    : [];
                              return included.length > 0
                                ? included.map((item, idx) => (
                                    <li key={idx} className="feature-item">
                                      <i className="fas fa-check text-danger me-2"></i>
                                      {typeof item === 'object'
                                        ? item.display_name || item.name || JSON.stringify(item)
                                        : item}
                                    </li>
                                  ))
                                : null;
                            })()}
                          </ul>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        <div className="product-document-pricing-cta">
          <Link to="/register?module=document" className="subscribe-now-btn">
            Subscribe Now
          </Link>
        </div>

        <p className="product-document-pricing-terms">
          Before continuing, ensure to read our{' '}
          <Link to="/terms-and-conditions" rel="noopener noreferrer" target="_blank" className="terms-link">
            terms and conditions
          </Link>
        </p>
      </div>
    </section>
  );
};

export default ProductDocumentPricing;
